import { InputNumber } from 'antd'
import { useMemo } from 'react'

import { Trans } from '@/components/Trans'
// import ResetToDefault from '@/assets/icons/reset.svg?react'
// import { Button } from '@/components/base/Button'
import { usePermissionForm } from '@/pages/teams/permissionQuota/PermissionFormContext'

// import { OtherQuotas } from '../../member/fake.memberPermission'

const GiB = 1024 ** 3

const toGiB = (bytes: number | null | undefined) =>
  typeof bytes === 'number' && bytes > 0 ? Math.floor(bytes / GiB) : 0

const toBytes = (gib: number | null | undefined) =>
  typeof gib === 'number' && gib >= 0 ? Math.floor(gib * GiB) : 0

export function Others() {
  const { scope, form, setForm, teamDefaults, quotaLimits } = usePermissionForm()

  const hints = useMemo(() => {
    const t = teamDefaults
    return {
      meshNet: t?.meshNetQuota,
      meshEndpoint: t?.meshEndpointQuota,
      instanceQty: t?.instanceQtyQuota,
      snapQty: t?.snapQtyQuota,
      storageGiB: toGiB(t?.storageCapBytesQuota ?? 0),
    }
  }, [teamDefaults])

  const set = <K extends keyof typeof form>(key: K, val: number) => {
    setForm({ ...form, [key]: val })
  }

  const max = {
    meshNet: quotaLimits?.meshNet ?? 10,
    snapQty: quotaLimits?.snapQty ?? 200,
    storageGiB: toGiB(quotaLimits?.storageBytes ?? 2199023255552),
  }

  return (
    <div className="mt-10 mb-4">
      <div className="flex items-center gap-x-2">
        <div className="h-[20px] border-secondary border-solid border-l-4" />
        <span className="h6 text-primary">
          <Trans i18nKey="team.permissionQuota.others">Others</Trans>
        </span>
        {/* <Button
          type="link"
          onClick={onReset}
          className="t-sb text-btn-primary bg-btn-primary/10 w-40 h-8 p-1 text-center flex items-center justify-center gap-x-1"
        >
          <ResetToDefault /> <Trans i18nKey="member.resetToDefault">Reset to Default</Trans>
        </Button> */}
      </div>
      <div className="mt-8 grid grid-cols-2 gap-6">
        <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="team.permissionQuota.snapQtyQuota">Snapshots Quota</Trans>
          </div>
          <InputNumber
            min={0}
            className="w-full h-11 flex items-center t-sb"
            value={form.snapQtyQuota}
            onChange={(v) => set('snapQtyQuota', Number(v ?? 0))}
            placeholder="0"
            parser={(v) => (v ? Number(v.replace(/[^\d]/g, '')) : 0)}
            onKeyDown={(e) => {
              if (
                e.key === '.' ||
                e.key === '-' ||
                e.key === '+' ||
                e.key === 'e' ||
                e.key === 'E'
              ) {
                e.preventDefault()
              }
            }}
          />
          {scope === 'member' && teamDefaults && (
            <div className="mt-1 t-sm text-secondary">Team default: {hints.snapQty}</div>
          )}
          {form.snapQtyQuota > max.snapQty && (
            <div className="mt-1 t-sm text-error">
              <Trans i18nKey="team.permissionQuota.maxSnapQty" values={{ max: max.snapQty }}>
                Maximum snapshot quota is {{ max }}.
              </Trans>
            </div>
          )}
        </div>

        <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="team.permissionQuota.storageCapBytesQuota">Storage Space Quota</Trans>
          </div>
          <InputNumber
            min={0}
            className="w-full h-11 flex items-center t-sb"
            value={toGiB(form.storageCapBytesQuota)}
            onChange={(v) => set('storageCapBytesQuota', toBytes(Number(v ?? 0)))}
            formatter={(v) => `${v ?? 0}`}
            parser={(v) => (v ? Number(v.replace(/[^\d]/g, '')) : 0)}
            suffix={<div className="t-sb text-secondary">GB</div>}
            onKeyDown={(e) => {
              if (
                e.key === '.' ||
                e.key === '-' ||
                e.key === '+' ||
                e.key === 'e' ||
                e.key === 'E'
              ) {
                e.preventDefault()
              }
            }}
          />
          {scope === 'member' && teamDefaults && (
            <div className="mt-1 t-sm text-secondary">Team default: {hints.storageGiB} GB</div>
          )}
          {toGiB(form.storageCapBytesQuota) > max.storageGiB && (
            <div className="mt-1 t-sm text-error">
              <Trans
                i18nKey="team.permissionQuota.maxStorageCapacity"
                values={{ max: max.storageGiB }}
              >
                Maximum storage capacity is {{ max }} GB.
              </Trans>
            </div>
          )}
        </div>
        <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="team.permissionQuota.meshNetQuota">Network Group Quota</Trans>
          </div>
          <InputNumber
            min={0}
            className="w-full h-11 flex items-center t-sb"
            value={form.meshNetQuota}
            onChange={(v) => set('meshNetQuota', Number(v ?? 0))}
            parser={(v) => (v ? Number(v.replace(/[^\d]/g, '')) : 0)}
            onKeyDown={(e) => {
              if (
                e.key === '.' ||
                e.key === '-' ||
                e.key === '+' ||
                e.key === 'e' ||
                e.key === 'E'
              ) {
                e.preventDefault()
              }
            }}
          />
          {scope === 'member' && teamDefaults && (
            <div className="mt-1 t-sm text-secondary">Team default: {hints.meshNet}</div>
          )}
          {form.meshNetQuota > max.meshNet && (
            <div className="mt-1 t-sm text-error">
              <Trans i18nKey="team.permissionQuota.maxMeshNetQuota" values={{ max: max.meshNet }}>
                Maximum mesh network quota is {{ max }}.
              </Trans>
            </div>
          )}
        </div>
        {/* todo: Auto Deploy */}
        {/* <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="member.autoDeploy">Auto Deploy Quota</Trans>
          </div>
          <Input
            min={0}
            className="w-full h-9"
            value={form.autoDeploy ?? undefined}
            onChange={(v) => setField('autoDeploy', typeof v === 'number' ? v : null)}
            placeholder="0"
          />
        </div> */}
        {/* todo: Auto Scaling */}
        {/* <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="member.autoScaling">Auto Scaling Quota</Trans>
          </div>
          <Input
            min={0}
            className="w-full h-9"
            value={form.autoScaling ?? undefined}
            onChange={(v) => setField('autoScaling', typeof v === 'number' ? v : null)}
            placeholder="0"
          />
        </div> */}
        {/* Network Group */}
        {/* <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="member.networkGroup">Network Group Quota</Trans>
          </div>
          <Input
            min={0}
            className="w-full h-9"
            value={form.networkGroupQuota ?? undefined}
            onChange={(v) => setField('networkGroup', typeof v === 'number' ? v : null)}
            placeholder="0"
          />
        </div> */}
        {/* todo: Public IP */}
        {/* <div>
          <div className="t-sb mb-2">
            <Trans i18nKey="member.publicIP">Public IP Quota</Trans>
          </div>
          <Input
            min={0}
            className="w-full h-9"
            value={form.publicIP ?? undefined}
            onChange={(v) => setField('publicIP', typeof v === 'number' ? v : null)}
            placeholder="0"
          />
        </div> */}
      </div>
    </div>
  )
}
